<script setup>
/**
 * GuideTour — Global guide tour component.
 *
 * Mounted once in AppLayout.vue. Manages the 8-step dashboard walkthrough
 * using driver.js.
 *
 * Flow:
 *   1. Reads `has_completed_guide_tour` from the Inertia dashboard page props.
 *   2. If not completed AND user is on the dashboard → shows the welcome modal.
 *   3. User clicks "Tour starten" → builds 8 steps and starts the driver.js tour.
 *   4. Steps 6 and 7 expand/collapse the sidebar via a custom DOM event
 *      (see SidebarContainer.vue for the listener).
 *   5. On skip or completion → POSTs to backend to mark tour as done.
 *
 * The header "Tour starten" button can re-trigger the tour by navigating to
 * the dashboard. That button is handled separately in HeaderContainer.vue.
 */
import { ref, watch, computed, nextTick, onMounted } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import StartGuideTourModal from './StartGuideTourModal.vue'

// ─── State ──────────────────────────────────────────────────────────────
const showModal = ref(false)
const tourActive = ref(false)
const preTourSidebarState = ref(null)

const page = usePage()
const dashboard = computed(() => page.props.dashboard)
const isDashboardPage = computed(() => {
    const url = page.url.split('?')[0]
    return url === '/dashboard'
})

// Detect "Tour starten" from header (via query param)
const startTourRequested = computed(() => {
    const url = new URL(window.location.href)
    return url.searchParams.has('start_tour')
})

// ─── Backend: mark tour as completed ────────────────────────────────────
const markTourCompleted = async () => {
    try {
        await router.post('/profiel/rondleiding/afgerond', {}, {
            preserveScroll: true,
            preserveState: true,
            only: ['dashboard'],
        })
    } catch {
        // Non-blocking: tour UI is already closed
    }
}

// ─── Sidebar toggle via custom event ────────────────────────────────────
// SidebarContainer.vue listens for this event to toggle its collapsed state
const setSidebarCollapsed = (collapsed) => {
    window.dispatchEvent(new CustomEvent('guide-tour:toggle-sidebar', {
        detail: { collapsed },
    }))
}

// ─── driver.js instance ─────────────────────────────────────────────────
let driverObj = null

/**
 * The 8 tour steps. Each step defines:
 *   - element: CSS selector to highlight (empty string = centered modal)
 *   - popover: title, description, position, and callbacks
 *
 * Step text matches the legacy exactly — no rewording.
 */
const buildSteps = () => [
    {
        popover: {
            title: 'Welkom bij WIZZR',
            description: 'Ontdek in een paar stappen de belangrijkste nieuwe functies en verbeteringen.',
            side: 'bottom',
            align: 'center',
        },
    },
    {
        element: '#dashboard-tile-persons-overview',
        popover: {
            title: 'Personen',
            description: 'Gebruik het plus-icoon op deze tegel om personen toe te voegen. Je kunt deze vervolgens koppelen aan een inkoopcontract voor verantwoording. Klik je op de tegel zelf, dan ga je naar de overzichtspagina met alle personen.',
            side: 'bottom',
            align: 'center',
        },
    },
    {
        element: '#dashboard-tile-social-return-orders',
        popover: {
            title: 'Sociaal Inkopen & MVO/SR Projecten',
            description: 'Het proces voor Sociaal Inkopen en MVO/SR Projecten is identiek aan Personen. Via de betreffende tegel kunt u Sociaal Inkopen dan wel MVO/SR projecten opvoeren om deze vervolgens op een inkoopcontract te kunnen verantwoorden',
            side: 'bottom',
            align: 'center',
        },
    },
    {
        element: '#dashboard-tile-matching',
        popover: {
            title: 'Verantwoording (Invoer koppelen aan inkoopcontract)',
            description: 'Nadat je een persoon, Sociaal Inkopen of MVO/SR Project hebt opgevoerd, verschijnt deze op de pagina "Verantwoording". Hier kun je de persoon, Sociaal Inkopen of MVO/SR Project koppelen aan een inkoopcontract. Bezoek de kennisbank voor uitgebreide instructies.',
            side: 'bottom',
            align: 'center',
        },
    },
    {
        element: '#button-header-support',
        popover: {
            title: 'Ondersteuning',
            description: 'De pagina Ondersteuning toont de contactgegevens van uw opdrachtgevers en intermediars. Daarnaast vind u hier een verwijzing naar de kennisbank en contactgegevens van WIZZR Support',
            side: 'bottom',
            align: 'center',
        },
    },
    {
        element: '#button-header-help',
        popover: {
            title: 'Hulpvraag',
            description: 'Heeft u vragen over de functionaliteiten van WIZZR? Via deze knop kunt u een hulpvraag indienen. Let op: de inhoud, het beleid en de uitvoering vallen onder de verantwoordelijkheid van uw opdrachtgever.',
            side: 'bottom',
            align: 'center',
            // Step 6: expand sidebar before moving to step 7
            onNextClick: () => {
                setSidebarCollapsed(false)
                setTimeout(() => driverObj.moveNext(), 300)
            },
        },
    },
    {
        element: '#sidebar-link-dashboard-index',
        popover: {
            title: 'Startscherm',
            description: 'Op dit scherm kunt u via de tegels naar verschillende pagina\'s navigeren.',
            side: 'right',
            align: 'center',
            // Step 7: collapse sidebar before moving to step 8; expand on going back
            onNextClick: () => {
                setSidebarCollapsed(true)
                setTimeout(() => driverObj.moveNext(), 300)
            },
            onPrevClick: () => {
                setSidebarCollapsed(false)
                setTimeout(() => driverObj.movePrev(), 300)
            },
        },
    },
    {
        element: '#sidebar-link-business-dashboard-index',
        popover: {
            title: 'Dashboard (WIZZR_Zakelijk)',
            description: 'Op het Dashboard krijg je inzicht in de maatschappelijke waarde van jouw organisatie en hoe je deze optimaal benut. Zakelijke gebruikers kunnen hier direct aan de slag. Interesse? Vraag vrijblijvend een proefabonnement aan.',
            side: 'right',
            align: 'center',
        },
    },
]

// ─── Start / skip ───────────────────────────────────────────────────────
const startTour = () => {
    showModal.value = false
    tourActive.value = true
    // Remember the sidebar state before the tour modifies it
    preTourSidebarState.value = document.getElementById('sidebarContainer')?.classList.contains('collapsed') ?? false

    driverObj = driver({
        showProgress: false,
        nextBtnText: 'Volgende',
        prevBtnText: 'Vorige',
        doneBtnText: 'Sluiten',
        closeBtnText: 'Tour overslaan',
        popoverClass: 'guide-tour-popover',
        onDestroyed: () => {
            // Called on ALL destruction paths: skip, close, or last step done.
            tourActive.value = false
            driverObj = null
            markTourCompleted()
            // Restore sidebar to its pre-tour state
            if (preTourSidebarState.value !== null) {
                setSidebarCollapsed(preTourSidebarState.value)
                preTourSidebarState.value = null
            }
        },
    })

    driverObj.setSteps(
        buildSteps().filter((step) => {
            if (!step.element) return true
            const el = document.querySelector(step.element)
            if (!el) {
                console.warn(`[GuideTour] Skipping step "${step.popover?.title}" — element "${step.element}" not found in DOM`)
                return false
            }
            return true
        })
    )

    nextTick(() => {
        driverObj.drive()
    })
}

const skipTour = () => {
    showModal.value = false
    markTourCompleted()
}

// ─── Auto-start on dashboard ────────────────────────────────────────────
const checkAutoStart = () => {
    if (!isDashboardPage.value) return
    if (tourActive.value) return

    // Header "Tour starten" button: always show modal, regardless of completion status
    if (startTourRequested.value) {
        showModal.value = true
        // Clean the URL so refreshing doesn't re-trigger
        window.history.replaceState({}, '', '/dashboard')
        return
    }

    // Auto-start for new users who haven't completed the tour
    if (!dashboard.value) return
    if (dashboard.value.has_completed_guide_tour) return

    showModal.value = true
}

// Re-check when navigating to the dashboard
watch(() => page.url, () => {
    nextTick(() => checkAutoStart())
})

onMounted(() => {
    // Delay to let ChangelogPopup render first; it will block until dismissed
    setTimeout(() => checkAutoStart(), 1000)
})
</script>

<template>
    <StartGuideTourModal
        :show="showModal"
        @start="startTour"
        @close="skipTour"
    />
</template>
